import {
  type NormalizedJobState,
  normalizeState,
} from "@/pages/pipelineHelpers";
import type { JobMetadata, WorkspaceSummary } from "@/types/api";

// The "Open pipeline" dropdown's rows (Figma 14894-169764): saved
// workspaces surface as drafts, deployed jobs under their live status.
// A workspace created from a job carries the job's name (the import
// recipe), so a name collision means "same pipeline" — the job row wins
// and the draft row folds into it. The list endpoint only serves
// {id, name} summaries, so the exact deployed_job pairing happens at
// open time, not here.
export type OpenPipelineItem =
  | { kind: "draft"; workspaceId: string; name: string }
  | { kind: "job"; name: string; state: NormalizedJobState };

export function buildOpenPipelineItems(
  workspaces: WorkspaceSummary[],
  jobs: JobMetadata[],
): OpenPipelineItem[] {
  const jobNames = new Set(jobs.map((j) => j.name));
  const items: OpenPipelineItem[] = [
    ...workspaces
      .filter((ws) => !jobNames.has(ws.name))
      .map((ws) => ({
        kind: "draft" as const,
        workspaceId: ws.id,
        name: ws.name,
      })),
    ...jobs.map((j) => ({
      kind: "job" as const,
      name: j.name,
      state: normalizeState(j.state),
    })),
  ];
  return items.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
}

export function filterOpenPipelineItems(
  items: OpenPipelineItem[],
  term: string,
): OpenPipelineItem[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return items;
  return items.filter((i) => i.name.toLowerCase().includes(needle));
}
